/* eslint-disable no-param-reassign */
import type { App } from 'vue';

import { CartStore } from '@common/contracts/server/ostin/api';

import { price } from '@/utils/filters/price';

import UiClientOnly from '../ui-kit/ui-client-only.vue';

// Directives
import reached from './directives/reached';
import tooltip from './directives/tooltip';
import media from './media/media.vue';
import Modal from './modal/modal.vue';
import ModalConfirm from './modal/modal-confirm.vue';
import RadioGroup from './radio-group/radio-group.vue';
import spriteIcon from './sprite-icon/sprite-icon.vue';
import Swiper from './swiper/swiper.vue';
import UiBadge from './ui-badge/ui-badge.vue';
import UiButton from './ui-button/ui-button.vue';
import UiCheckbox from './ui-checkbox/ui-checkbox.vue';
import UiCheckButton from './ui-checkbutton/ui-checkbutton.vue';
import UiCitySuggest from './ui-city-suggest/ui-city-suggest.vue';
import UiDateInput from './ui-date-input/ui-date-input.vue';
import UiDatepicker from './ui-datepicker/ui-datepicker.vue';
import UiDialog from './ui-dialog/ui-dialog.vue';
import UiDropdown from './ui-dropdown/ui-dropdown.vue';
import UiInput from './ui-input/ui-input.vue';
import UiLink from './ui-link/ui-link.vue';
import UiLoader from './ui-loader/ui-loader.vue';
import UiMarker from './ui-marker/ui-marker.vue';
import UiMetro from './ui-metro/ui-metro.vue';
import UiNotification from './ui-notification/ui-notification.vue';
import UiRadio from './ui-radio/ui-radio.vue';
import UiRadiocards from './ui-radiocards/ui-radiocards.vue';
import UiSegmented from './ui-segmented/ui-segmented.vue';
import UiSelect from './ui-select/ui-select.vue';
import UiStreetSuggest from './ui-street-suggest/ui-street-suggest.vue';
import UiTabs from './ui-tabs/ui-tabs.vue';
import UiTextarea from './ui-textarea/ui-textarea.vue';
import { decl } from './decl';
import { fullDate } from './full-date';
import { prettyPhone } from './pretty-phone';

// TODO: Покрыть тестами вдоль и в поперёк (вынести в utils?)
const date = (d: string, { showYear = false } = {}) => { // преобразует в формат "ХХ месяц ХХХХ"
  if (!d) return '';

  const msPerDay = 24 * 60 * 60 * 1000;
  const daysDiff = Math.ceil(((new Date(d)).getTime() - Date.now()) / msPerDay);
  const humanDays = ['сегодня', 'завтра'];
  if (humanDays[daysDiff]) {
    return humanDays[daysDiff];
  }
  const months = ['января', 'февраля', 'марта', 'апреля', 'мая', 'июня', 'июля', 'августа', 'сентября', 'октября', 'ноября', 'декабря'];
  // eslint-disable-next-line no-unused-vars
  const [_date] = d.split('T');
  const [year, month, day] = _date.split('-');
  return `${Math.round(day)} ${months[month - 1]} ${showYear ? year : ''}`;
};

// TODO: Покрыть тестами вдоль и в поперёк (вынести в utils?)
export const parseWorkSchedule = (store: CartStore, isReplaceSpace = true): string => {
  if (store.status === 'closed') {
    return 'Временно закрыт';
  }

  if (!store.workSchedule) {
    return '';
  }

  return store.workSchedule.reduce<string[]>((acc, { workDays, workTime }) => {
    const space = isReplaceSpace ? '&nbsp;' : ' ';
    const days = workDays?.replace(/\s/, space) ?? '';
    const time = workTime?.replace(/\s/, space) ?? '';

    if (days && time) {
      acc.push(`${days}:${space}${time}`);
    } else if (time) {
      acc.push(time);
    } else {
      acc.push(days);
    }
    return acc;
  }, [])
    .join(', ')
    .toLowerCase();
};

// TODO: Покрыть тестами вдоль и в поперёк (вынести в utils?)
const noBracesPhone = (phone) => phone?.replace(/[(, )]+/g, ' ') || '';

// TODO: Покрыть тестами вдоль и в поперёк (вынести в utils?)
const mask = (value = '', _mask = '######', keepMasked = true) => {
  const tokens = {
    '#': { pattern: /\d/ },
  };

  let iMask = 0;
  let iValue = 0;
  let output = '';
  while (iMask < _mask.length && iValue < value.length) {
    const cMask = _mask[iMask];
    const masker = tokens[cMask];
    const cValue = value[iValue];
    if (masker) {
      if (masker.pattern.test(cValue)) {
        output += masker.transform ? masker.transform(cValue) : cValue;
        iMask += 1;
      }
      iValue += 1;
    } else {
      if (keepMasked) output += cMask;
      if (cValue === cMask) iValue += 1;
      iMask += 1;
    }
  }
  return output;
};

const filters = {
  price,
  decl,
  date,
  fullDate,
  mask,
  parseWorkSchedule,
  prettyPhone,
  noBracesPhone,
} as const;

declare module '@vue/runtime-core' {
  // eslint-disable-next-line @typescript-eslint/naming-convention
  interface ComponentCustomProperties {
    $filters: typeof filters;
  }
}

export default {
  install(app: App) {
    // TODO: Исправить vue/multi-word-component-names
    // eslint-disable-next-line vue/multi-word-component-names
    app.component('Media', media);
    app.component('FSpriteIcon', spriteIcon);
    app.component('UiDropdown', UiDropdown);
    app.component('UiLoader', UiLoader);
    app.component('UiButton', UiButton);
    app.component('UiBadge', UiBadge);
    app.component('UiInput', UiInput);
    app.component('UiTextarea', UiTextarea);
    app.component('UiMarker', UiMarker);
    app.component('UiMetro', UiMetro);
    app.component('UiCitySuggest', UiCitySuggest);
    app.component('UiStreetSuggest', UiStreetSuggest);
    app.component('UiDateInput', UiDateInput);
    app.component('UiDatepicker', UiDatepicker);
    app.component('UiSegmented', UiSegmented);
    app.component('UiTabs', UiTabs);
    app.component('UiSelect', UiSelect);
    app.component('UiRadiocards', UiRadiocards);
    app.component('UiRadio', UiRadio);
    app.component('RadioGroup', RadioGroup);
    app.component('UiNotification', UiNotification);
    app.component('UiCheckbox', UiCheckbox);
    app.component('UiCheckbutton', UiCheckButton);
    app.component('UiLink', UiLink);
    // TODO: Исправить vue/multi-word-component-names
    // eslint-disable-next-line vue/multi-word-component-names
    app.component('Swiper', Swiper);
    // TODO: Исправить vue/multi-word-component-names
    // eslint-disable-next-line vue/multi-word-component-names
    app.component('Modal', Modal);
    app.component('ModalConfirm', ModalConfirm);
    app.component('UiDialog', UiDialog);
    app.component('ClientOnly', UiClientOnly);

    app.directive('reached', reached);
    app.directive('tooltip', tooltip);

    app.config.globalProperties.$filters = filters;
  },
};
